/**
 * AdminObondoCard — the super-admin's dedicated AI platform-management
 * assistant. Same persona name as the trader-facing Obondo, but with
 * a read-only system prompt scoped to platform ops (users, revenue,
 * feedback, sessions).
 *
 * Endpoint: POST /admin/obondo/chat
 * Session persists across turns until the admin clicks "New session".
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Send, Sparkles, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

const SUGGESTED = [
    "How many new signups in the last 24 hours?",
    "Give me a snapshot of my institutional pipeline.",
    "What's on the top-5 leaderboard right now?",
    "Any unresolved feedback waiting for review?",
    "How do I broadcast a message to all users?",
];

export default function AdminObondoCard() {
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content:
                "Habari, boss. I'm Obondo — your platform assistant. Ask me about users, revenue, feedback, or which admin tab handles what. I read the live platform snapshot before every reply.",
        },
    ]);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [snapshot, setSnapshot] = useState(null);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [messages]);

    async function send(prompt) {
        const text = (prompt ?? input).trim();
        if (!text || busy) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: text }]);
        setBusy(true);
        try {
            const { data } = await api.post("/admin/obondo/chat", {
                message: text,
                session_id: sessionId,
            });
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
            setSessionId(data.session_id);
            setSnapshot(data.snapshot);
        } catch (e) {
            const detail = e.response?.data?.detail || "Obondo could not reply";
            toast.error(detail);
            setMessages((m) => [
                ...m,
                { role: "assistant", content: `_Error: ${detail}_`, error: true },
            ]);
        } finally { setBusy(false); }
    }

    function reset() {
        setSessionId(null);
        setMessages([
            {
                role: "assistant",
                content: "Fresh session. What are we digging into next?",
            },
        ]);
        setSnapshot(null);
    }

    return (
        <div className="grid md:grid-cols-[1fr,300px] gap-4" data-testid="admin-obondo-card">
            {/* Chat column */}
            <div className="border border-border bg-card rounded flex flex-col h-[560px]">
                <div className="border-b border-border p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-black text-sm">O</div>
                        <div>
                            <div className="text-sm font-extrabold leading-tight">Obondo (admin)</div>
                            <div className="text-[10px] text-muted-foreground leading-tight">
                                Read-only platform assistant · GPT-5.2
                            </div>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={reset} data-testid="obondo-admin-reset">
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> New session
                    </Button>
                </div>

                <div
                    ref={scrollRef}
                    className="flex-1 overflow-y-auto p-3 space-y-3"
                    data-testid="obondo-admin-transcript"
                >
                    {messages.map((m, i) => (
                        <div
                            key={m.id || m.ts || `msg-${i}`}
                            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                            <div
                                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                                    m.role === "user"
                                        ? "bg-primary text-primary-foreground"
                                        : m.error
                                        ? "bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/30"
                                        : "bg-secondary text-foreground"
                                }`}
                            >
                                {m.content}
                            </div>
                        </div>
                    ))}
                    {busy && (
                        <div className="flex justify-start">
                            <div className="bg-secondary rounded-lg px-3 py-2 text-sm text-muted-foreground">
                                Obondo is thinking…
                            </div>
                        </div>
                    )}
                </div>

                {/* Suggested prompts */}
                {messages.length <= 2 && (
                    <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                        {SUGGESTED.map((s) => (
                            <button
                                key={s}
                                onClick={() => send(s)}
                                disabled={busy}
                                data-testid={`obondo-admin-suggested-${s.slice(0, 15).replace(/\s+/g, "-")}`}
                                className="text-[10px] border border-border rounded-full px-2 py-1 text-muted-foreground hover:border-primary hover:text-primary transition"
                            >
                                <Sparkles className="w-2.5 h-2.5 inline mr-0.5" />
                                {s}
                            </button>
                        ))}
                    </div>
                )}

                <form
                    onSubmit={(e) => { e.preventDefault(); send(); }}
                    className="border-t border-border p-3 flex gap-2"
                >
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder="Ask Obondo about the platform…"
                        disabled={busy}
                        data-testid="obondo-admin-input"
                        className="flex-1 h-10 bg-background border border-border rounded px-3 text-sm focus:border-primary focus:outline-none"
                    />
                    <Button type="submit" disabled={busy || !input.trim()} data-testid="obondo-admin-send">
                        <Send className="w-4 h-4" />
                    </Button>
                </form>
            </div>

            {/* Live snapshot sidebar */}
            <div className="border border-border bg-card rounded p-3 h-[560px] overflow-y-auto">
                <div className="overline mb-2">Live snapshot</div>
                {snapshot ? (
                    <div className="text-xs space-y-3 mono">
                        <Section title="Users">
                            <Row k="total"       v={snapshot.users?.total?.toLocaleString()} />
                            <Row k="individuals" v={snapshot.users?.individuals?.toLocaleString()} />
                            <Row k="MoE seats"   v={snapshot.users?.moe_seats?.toLocaleString()} />
                            <Row k="CMA seats"   v={snapshot.users?.cma_seats?.toLocaleString()} />
                            <Row k="frozen"      v={snapshot.users?.frozen?.toLocaleString()} />
                            <Row k="new 24h"     v={snapshot.users?.new_24h?.toLocaleString()} />
                            <Row k="new 7d"      v={snapshot.users?.new_7d?.toLocaleString()} />
                        </Section>
                        <Section title="Agencies">
                            {Object.entries(snapshot.agencies || {}).map(([k, v]) => (
                                <Row key={k} k={k} v={String(v)} />
                            ))}
                        </Section>
                        <Section title="Operations">
                            <Row k="logins 24h"   v={snapshot.logins_24h?.toLocaleString()} />
                            <Row k="open subs"    v={snapshot.open_sub_reqs?.toLocaleString()} />
                            <Row k="open feedback" v={snapshot.open_feedback?.toLocaleString()} />
                        </Section>
                    </div>
                ) : (
                    <div className="text-[11px] text-muted-foreground">
                        The snapshot loads with the first reply. Send Obondo a question to populate.
                    </div>
                )}
            </div>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{title}</div>
            <div className="space-y-0.5">{children}</div>
        </div>
    );
}
function Row({ k, v }) {
    return (
        <div className="flex justify-between border-b border-border/50 py-0.5">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-bold">{v || "—"}</span>
        </div>
    );
}
